import apiClient from './api';
import { API_ROUTES } from '@/config/routes';
import { setTokens, setUser, clearAuth } from '@/utils/auth';

/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

export const authService = {
  /**
   * Login user
   */
  async login(credentials) {
    const response = await apiClient.post(API_ROUTES.AUTH.LOGIN, credentials);
    
    if (response.data.success) {
      const { token, data } = response.data;
      setTokens(token);
      setUser(data);
      return response.data;
    }
    
    throw new Error(response.data.message || 'Login failed');
  },

  /**
   * Register new user
   */
  async register(userData) {
    const response = await apiClient.post(API_ROUTES.AUTH.REGISTER, userData);
    
    if (response.data.success) {
      const { token, data } = response.data;
      setTokens(token);
      setUser(data);
      return response.data;
    }
    
    throw new Error(response.data.message || 'Registration failed');
  },

  /**
   * Google OAuth Login/Register
   */
  async googleAuth(googleData) {
    const response = await apiClient.post(API_ROUTES.AUTH.GOOGLE, googleData);
    
    if (response.data.success) {
      const { token, data } = response.data;
      setTokens(token);
      setUser(data);
      return response.data;
    }
    
    throw new Error(response.data.message || 'Google authentication failed');
  },

  /**
   * Check whether an email is already used
   */
  async checkEmailAvailability(email) {
    const response = await apiClient.get(API_ROUTES.AUTH.CHECK_EMAIL, {
      params: { email },
    });

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to check email availability');
  },

  /**
   * Send reset OTP to email
   */
  async forgotPassword(email) {
    const response = await apiClient.post(API_ROUTES.AUTH.FORGOT_PASSWORD, { email });

    if (response.data.success) {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to send reset OTP');
  },

  /**
   * Verify reset OTP
   */
  async verifyResetOtp(email, otp) {
    const response = await apiClient.post(API_ROUTES.AUTH.VERIFY_RESET_OTP, { email, otp });

    if (response.data.success) {
      return response.data;
    }

    throw new Error(response.data.message || 'Invalid OTP');
  },

  /**
   * Reset password with email and OTP
   */
  async resetPassword({ email, otp, newPassword }) {
    const response = await apiClient.post(API_ROUTES.AUTH.RESET_PASSWORD, {
      email,
      otp,
      new_password: newPassword,
    });

    if (response.data.success) {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to reset password');
  },

  /**
   * Create admin account from admin panel
   */
  async createAdmin(payload) {
    const response = await apiClient.post(API_ROUTES.AUTH.CREATE_ADMIN, payload);

    if (response.data.success) {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to create admin');
  },

  /**
   * Get all admin accounts
   */
  async getAdmins() {
    const response = await apiClient.get(API_ROUTES.AUTH.ADMINS);

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch admins');
  },

  /**
   * Update an admin account
   */
  async updateAdmin(id, payload) {
    const response = await apiClient.put(API_ROUTES.AUTH.UPDATE_ADMIN(id), payload);

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to update admin');
  },

  /**
   * Delete an admin account
   */
  async deleteAdmin(id) {
    const response = await apiClient.delete(API_ROUTES.AUTH.DELETE_ADMIN(id));

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to delete admin');
  },

  /**
   * Logout user
   */
  async logout() {
    try {
      await apiClient.post(API_ROUTES.AUTH.LOGOUT);
    } finally {
      clearAuth();
    }
  },

  /**
   * Get current user profile
   */
  async getProfile() {
    const response = await apiClient.get(API_ROUTES.AUTH.PROFILE);
    
    if (response.data.success) {
      setUser(response.data.data);
      return response.data;
    }
    
    throw new Error(response.data.message || 'Failed to get profile');
  },
};

export default authService;
